import { useCallback, useEffect, useRef, useState } from 'react';

import mime from 'mime';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/plugins/record';

const SCROLLING_WAVEFORM_WINDOW = 5;
const MS_PER_SECOND = 1000;

const WAVEFORM_OPTIONS = {
  barWidth: 2,
  barGap: 1,
  height: 32,
  cursorWidth: 0,
  normalize: true,
} as const;

/** Transcodes an audio blob to MP3 via the server. */
const transcodeToMp3 = async (blob: Blob): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', blob, `audio.${mime.getExtension(blob.type)}`);
  const response = await fetch('/www/media/convert-audio', { method: 'POST', body: formData });
  if (!response.ok) {
    throw new Error(`Transcoding failed: ${response.status}`);
  }
  return response.blob();
};

export interface UseWaveformRecorderReturn {
  containerRef: (node: HTMLDivElement | null) => void;
  startRecording: () => void;
  stopRecording: () => void;
  isRecording: boolean;
  recordingTime: number;
  totalRecordingTime: number;
  recordingBlob: Blob | undefined;
  setRecordingBlob: (blob: Blob | undefined) => void;
  isTranscoding: boolean;
  isPlaying: boolean;
  isReady: boolean;
  currentTime: number;
  duration: number;
  playPause: () => void;
  reset: () => void;
}

/**
 * Wraps wavesurfer.js + RecordPlugin for recording, live waveform,
 * transcoding, and post-recording playback.
 *
 * Uses a callback ref so the wavesurfer instance is created only when
 * the container div mounts (the div is conditionally rendered).
 */
export const useWaveformRecorder = (): UseWaveformRecorderReturn => {
  const { t } = useTranslation();

  // Callback-ref state: drives wavesurfer creation when the DOM node appears
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const recordRef = useRef<RecordPlugin | null>(null);
  const cancelledRef = useRef(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const pendingStartRef = useRef(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [totalRecordingTime, setTotalRecordingTime] = useState(0);

  // Post-recording state
  const [recordingBlob, setRecordingBlob] = useState<Blob>();
  const [isTranscoding, setIsTranscoding] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Callback ref — triggers wavesurfer (re)creation when the div mounts.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  // Create / destroy wavesurfer when the container appears / disappears
  useEffect(() => {
    if (!container) {
      return;
    }

    const record = RecordPlugin.create({
      renderRecordedAudio: true,
      scrollingWaveform: true,
      scrollingWaveformWindow: SCROLLING_WAVEFORM_WINDOW,
    });

    const styles = getComputedStyle(container);
    const ws = WaveSurfer.create({
      container,
      ...WAVEFORM_OPTIONS,
      waveColor: styles.getPropertyValue('--muted-foreground').trim(),
      progressColor: styles.getPropertyValue('--primary').trim(),
      plugins: [record],
    });

    wsRef.current = ws;
    recordRef.current = record;
    cancelledRef.current = false;

    // Record events
    record.on('record-progress', (durationMs: number) => {
      setRecordingTime(Math.round(durationMs / MS_PER_SECOND));
    });

    record.on('record-end', async (blob: Blob) => {
      setTotalRecordingTime(Math.round(record.getDuration() / MS_PER_SECOND));
      setRecordingTime(0);
      setIsRecording(false);

      // RecordPlugin calls ws.load(blobURL) without awaiting after this handler.
      // Prime the media element once that load completes so the first play works.
      ws.once('ready', () => ws.seekTo(0));

      try {
        setIsTranscoding(true);
        const mp3 = await transcodeToMp3(blob);
        if (cancelledRef.current) {
          return;
        }
        setRecordingBlob(mp3);
      } catch {
        toast.error(t('chatbox.recording_error'));
      } finally {
        if (!cancelledRef.current) {
          setIsTranscoding(false);
        }
      }
    });

    // Playback events (post-recording waveform)
    ws.on('ready', () => setIsReady(true));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('timeupdate', (time: number) => setCurrentTime(time));
    ws.on('decode', (dur: number) => setDuration(dur));
    ws.on('finish', () => {
      setCurrentTime(0);
      ws.seekTo(0);
    });

    // Auto-start recording if startRecording() was called before the container mounted
    if (pendingStartRef.current) {
      pendingStartRef.current = false;
      record.startRecording().catch(() => {
        setIsRecording(false);
      });
    }

    return () => {
      cancelledRef.current = true;
      if (record.isRecording()) {
        record.stopRecording();
      }
      ws.destroy();
      wsRef.current = null;
      recordRef.current = null;
      setIsReady(false);
      setIsPlaying(false);
    };
  }, [container]);

  const startRecording = useCallback(() => {
    setRecordingBlob(undefined);
    setTotalRecordingTime(0);
    setCurrentTime(0);
    setDuration(0);
    setIsRecording(true);
    pendingStartRef.current = true;
  }, []);

  const stopRecording = useCallback(() => {
    recordRef.current?.stopRecording();
  }, []);

  const playPause = useCallback(() => {
    wsRef.current?.playPause();
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    setRecordingBlob(undefined);
    setIsRecording(false);
    pendingStartRef.current = false;
    setRecordingTime(0);
    setTotalRecordingTime(0);
    setCurrentTime(0);
    setDuration(0);
    setIsTranscoding(false);
    setIsPlaying(false);
    setIsReady(false);
  }, []);

  return {
    containerRef,
    startRecording,
    stopRecording,
    isRecording,
    recordingTime,
    totalRecordingTime,
    recordingBlob,
    setRecordingBlob,
    isTranscoding,
    isPlaying,
    isReady,
    currentTime,
    duration,
    playPause,
    reset,
  };
};
